import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../../components/TopBar";
import EmptyState from "../../components/EmptyState";
import { api, fmtUSD, todayISO } from "../../lib/api";
import { Receipt, Plus, Pencil, Trash2, CheckCircle2, Printer } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "bg-[#F5F4F0] text-[#66645E]",
  sent: "bg-[#1F1E1B] text-white",
  paid: "bg-[#1A4135] text-white",
  overdue: "bg-[#BC4727] text-white",
  void: "bg-[#9E9B90] text-white",
};

export default function Invoices({ businessId, title = "Invoices", subtitle }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    const params = new URLSearchParams({ business_id: businessId });
    if (statusFilter) params.set("status", statusFilter);
    const { data } = await api.get(`/invoices?${params.toString()}`);
    setRows(data);
  };

  useEffect(() => { load(); }, [businessId, statusFilter]);

  const totals = useMemo(() => {
    const sum = (s) => rows.filter((r) => s === "all" || r.status === s).reduce((a, b) => a + (b.total || 0), 0);
    return {
      outstanding: rows.filter((r) => ["sent", "overdue"].includes(r.status)).reduce((a, b) => a + (b.total || 0), 0),
      paid: sum("paid"),
      draft: sum("draft"),
      all: sum("all"),
    };
  }, [rows]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`); toast.success("Deleted"); load();
  };

  const markPaid = async (inv) => {
    if (!window.confirm(`Mark ${inv.invoice_number} as PAID?`)) return;
    await api.post(`/invoices/${inv.id}/mark-paid`, null, { params: { paid_at: todayISO() } });
    toast.success("Marked paid"); load();
  };

  return (
    <div>
      <TopBar
        title={title}
        subtitle={subtitle || "Invoices for this business — printable and ready for the books."}
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button
              data-testid="add-invoice-btn"
              onClick={() => navigate(`new`)}
              className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#A03B1F] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Invoice
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="invoice-stats">
        <Card label="Outstanding" value={totals.outstanding} tone="warning" />
        <Card label="Paid" value={totals.paid} tone="positive" />
        <Card label="Draft" value={totals.draft} tone="muted" />
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">Filter Status</div>
          <select data-testid="invoice-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="void">Void</option>
          </select>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" hint="Click New Invoice to bill a customer." testid="empty-invoices" />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
          <table className="ledger-table" data-testid="invoices-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Due</th>
                <th>Status</th>
                <th className="num">Total</th>
                <th style={{ width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`invoice-row-${r.id}`}>
                  <td className="font-bold">{r.invoice_number}</td>
                  <td className="tabular">{r.issue_date}</td>
                  <td>{r.customer_name || "—"}</td>
                  <td className="tabular text-[#66645E]">{r.due_date || "—"}</td>
                  <td><span className={`text-[10px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span></td>
                  <td className="num font-bold">{fmtUSD(r.total)}</td>
                  <td>
                    <div className="flex gap-1.5 justify-end">
                      <button data-testid={`print-invoice-${r.id}`} onClick={() => navigate(`${r.id}/print`)} title="View / Print" className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Printer className="w-3 h-3" /></button>
                      <button data-testid={`edit-invoice-${r.id}`} onClick={() => navigate(`${r.id}`)} title="Edit" className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                      {r.status !== "paid" && (
                        <button data-testid={`mark-paid-${r.id}`} onClick={() => markPaid(r)} title="Mark paid" className="p-1.5 border border-[#1A4135] text-[#1A4135] hover:bg-[#1A4135] hover:text-white"><CheckCircle2 className="w-3 h-3" /></button>
                      )}
                      <button data-testid={`delete-invoice-${r.id}`} onClick={() => onDelete(r.id)} title="Delete" className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, tone }) {
  const color = tone === "positive" ? "text-[#1A4135]" : tone === "warning" ? "text-[#BC4727]" : tone === "muted" ? "text-[#66645E]" : "text-[#1F1E1B]";
  return (
    <div className="bg-white border border-[#1F1E1B] p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-3xl font-black tabular mt-1 ${color}`}>{fmtUSD(value)}</div>
    </div>
  );
}
